import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import PropTypes from 'prop-types'

import PortalModal from 'components/Shared/PortalModal'
import CampaignBudget from 'components/Recommendations/CampaignBudget'
import CampaignGoals from 'components/Recommendations/CampaignGoals'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { SecondaryButton } from 'components/Shared/Button'
import Fade from 'components/Shared/Wrappers/Fade'
import { hasRunningCampaigns, uniqueTasteClusterCount, getCampaignRuntime } from 'api/models'
import OpenCampaigns from './OpenCampaigns'
import { eventApi, fetchEventBudgetMinMax, fetchEventSuggestedTasteClusters } from 'api/events'
import { initStateObject, objectReducer } from 'reducers/default'
import CampaignRuntime from './CampaignRuntime'
import InfoBox from 'components/Shared/InfoBox'
import useIsFirstRender from 'hooks/useFirstRender'
import { useItcStatus } from 'components/Recommendations/Itc/ItcStatusContextProvider'
import useHasPermission, { Permissions } from 'hooks/useHasPermission'
import {
  useCampaignSetupDispatch,
  useCampaignSetup,
  useCurrentStepDispatch,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import useCustomTranslation from 'hooks/useCustomTranslation'
import ConfirmDeselectTcModal from './ConfirmDeselectTcModal'
const calculateTotalRuntime = (startDate, endDate) => {
  return Math.round(new moment.duration(endDate - startDate).asDays())
}

const CampaignSetup = ({
  event,
  campaigns,
  suggestedTcs,
  selectedTcs,
  setSelectedTcs,
  initialSelectedTcs = [],
  initialBudget,
  initialGoal,
  dispatchSuggestedTcsAction,
  openCampaigns,
  refetchCampaigns,
  setSuggestedTcsChanged = () => {},
}) => {
  const { t } = useTranslation()
  const { setup } = useCampaignSetup()
  const isCampaignSetupInProgress = useMemo(() => !!setup, [setup])

  const { startSetup, reconfigureSetup } = useCampaignSetupDispatch()
  const { goToNextStep } = useCurrentStepDispatch()
  const isFirstRender = useIsFirstRender()
  const hasWaveAccess = useHasPermission(Permissions.wave)
  const [budgetRange, dispatchBudgetRangeAction] = useReducer(objectReducer, initStateObject)
  const budgetLoadingRef = React.useRef(false)
  const defaultIntegrationDetails = React.useRef({
    integration_details: [],
    conversion_id: '',
    custom_event_type: '',
    lead_form_id: '',
  })
  const {
    mappedStatus: itcMappedStatus,
    loading: itcStatusLoading,
    error: itcStatusError,
  } = useItcStatus()
  const [eventBudget, setEventBudget] = useState(setup?.total_budget || initialBudget)
  const [eventGoal, setEventGoal] = useState(() => {
    if (!isCampaignSetupInProgress) return initialGoal

    const goalParts = setup?.goal.split('.')
    return goalParts[goalParts.length - 1]
  })

  useEffect(() => {
    if (!isCampaignSetupInProgress) {
      setEventGoal(initialGoal)
    }
  }, [initialGoal, isCampaignSetupInProgress])

  const eventStartDate = moment(event.date)
  const showShortNoticeWarning = eventStartDate.diff(moment(), 'days') <= 7
  const totalUniqueTcs = uniqueTasteClusterCount(suggestedTcs.items)
  const [totalRuntime, setTotalRuntime] = useState(null)
  const [dates, setDates] = useState({
    from: null,
    to: null,
  })
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [deselectedTcs, setDeselectedTcs] = useState([])
  const { getTranslation } = useCustomTranslation(event)
  useEffect(() => {
    if (typeof totalRuntime === 'number') {
      fetchEventBudgetMinMax(dispatchBudgetRangeAction, event.id, totalRuntime)
    }
  }, [event.id, totalRuntime])

  useEffect(() => {
    fetchEventSuggestedTasteClusters(dispatchSuggestedTcsAction, event.id, eventBudget, eventGoal)
  }, [event.id, eventBudget, eventGoal, dispatchSuggestedTcsAction])

  useEffect(() => {
    if (!isFirstRender) setSuggestedTcsChanged(true)
  }, [event.id, eventBudget, eventGoal, setSuggestedTcsChanged, isFirstRender])

  useEffect(() => {
    const [runtimeStartDate, runtimeEndDate] = getCampaignRuntime(event)
    const todayDate = new Date()
    const startDate =
      setup?.start_date && new Date(setup.start_date) > todayDate
        ? new Date(setup.start_date)
        : runtimeStartDate.toDate()
    const endDate = setup?.end_date ? new Date(setup.end_date) : runtimeEndDate.toDate()

    setDates({
      from: startDate,
      to: endDate,
    })
    setTotalRuntime(calculateTotalRuntime(startDate, endDate))
  }, [setup, event])

  useEffect(() => {
    if (defaultIntegrationDetails.current.integration_details.length > 0) {
      return
    }

    eventApi.fetchEventDefaultCampaignParameters(event.id).then((response) => {
      if (response.status === 200 && response.data.integration_details?.length > 0) {
        defaultIntegrationDetails.current = {
          integration_details: response.data.integration_details,
          conversion_id: response.data.conversion_id,
          lead_form_id: response.data.lead_form_id,
          custom_event_type: response.data.custom_event_type,
        }
      }
    })
  }, [event.id])

  const updateDate = (date, dateRangeString) => {
    const formattedDate = date ? moment(date).toDate() : ''

    const currentDateTime = { ...dates }
    currentDateTime[dateRangeString] = formattedDate
    const runtimeDuration = calculateTotalRuntime(currentDateTime.from, currentDateTime.to)

    setDates(currentDateTime)
    setTotalRuntime(runtimeDuration)
  }

  const startOrContinueSetup = useCallback(() => {
    if (itcStatusError || itcStatusLoading || ['running', 'failed'].includes(itcMappedStatus)) {
      return
    }

    const selectedCampaigns = campaigns.items.filter((x) => selectedTcs.includes(x.tc))

    let config = {
      total_budget: eventBudget,
      goal: eventGoal,
      start_date: dates.from?.toISOString(),
      end_date: dates.to?.toISOString(),
      creatives: selectedCampaigns.map((x) => ({
        tc: x.tc,
        tc_run_id: x.tc_run_id,
      })),
    }

    if (!isCampaignSetupInProgress) {
      config = {
        ...config,
        integration_details: defaultIntegrationDetails.current.integration_details,
        conversion_id: defaultIntegrationDetails.current.conversion_id,
        lead_form_id: defaultIntegrationDetails.current.lead_form_id,
        custom_event_type: defaultIntegrationDetails.current.custom_event_type,
      }
    }

    if (isCampaignSetupInProgress) {
      reconfigureSetup(config, (result) => {
        if (result.success) {
          goToNextStep()
        }
      })
    } else {
      startSetup(config, (result) => {
        if (result.success) {
          goToNextStep()
        }
      })
    }
  }, [
    eventBudget,
    eventGoal,
    dates,
    selectedTcs,
    campaigns.items,
    goToNextStep,
    isCampaignSetupInProgress,
    itcMappedStatus,
    itcStatusError,
    itcStatusLoading,
    reconfigureSetup,
    startSetup,
  ])

  const handleNextClick = useCallback(() => {
    const deselectedTcs =
      Array.isArray(initialSelectedTcs) && initialSelectedTcs.length
        ? initialSelectedTcs.filter((tc) => !selectedTcs.includes(tc))
        : []

    if (deselectedTcs.length) {
      setDeselectedTcs(deselectedTcs)
      setIsConfirmModalOpen(true)
      return
    }

    startOrContinueSetup()
  }, [initialSelectedTcs, selectedTcs, startOrContinueSetup])

  const eventHasRunningCampaigns = useMemo(
    () => hasRunningCampaigns(campaigns.items),
    [campaigns.items],
  )
  return (
    <div className="d-flex flex-column gap-5 p-0">
      <Fade transitionDuration={500}>
        <div className="d-flex flex-column">
          {showShortNoticeWarning && (
            <TipDialogueBox
              title={t('Recommendations.shortNoticeWarning.title')}
              text={getTranslation('Recommendations.shortNoticeWarning.content')}
              variant="warning-light"
            />
          )}

          <div className="d-flex flex-column flex-lg-row my-1 order-2 order-lg-1">
            <div className="flex-1 col-12 col-lg-5 px-0 mb-3 mb-lg-0">
              <CampaignGoals
                event={event}
                budget={eventBudget}
                eventId={event.id}
                onGoalSelected={setEventGoal}
                selectedGoal={eventGoal}
                disabled={eventHasRunningCampaigns}
              />
            </div>
            <div className="flex-1 col-12 col-lg-7 px-0 pl-lg-3">
              <CampaignBudget
                selectedBudget={eventBudget}
                minimumBudget={budgetRange.content.budget_abs_min || eventBudget}
                maximumBudget={budgetRange.content.budget_max}
                recommendedMinimumBudget={budgetRange.content.budget_min}
                onChangeQueued={() => (budgetLoadingRef.current = true)}
                onBudgetChanged={(b) => {
                  setEventBudget(b)
                  budgetLoadingRef.current = false
                }}
                addressedTasteClusters={uniqueTasteClusterCount(suggestedTcs.items)}
              >
                <InfoBox
                  title={t('Tips.Recommendations.CampaignBudget.title')}
                  body={
                    <div>
                      <p>{getTranslation('Tips.Recommendations.CampaignBudget.body1')}</p>
                      <br />
                      <p>{t('Tips.Recommendations.CampaignBudget.body2')}</p>
                    </div>
                  }
                  placement="bottom"
                >
                  <h2 className="card-title-default d-inline-block event-header-title">
                    {t(
                      `Recommendations.campaignBudget.${eventHasRunningCampaigns ? 'selectBudgetForNewAudiences' : 'selectLabel'}`,
                    )}
                  </h2>
                </InfoBox>
              </CampaignBudget>
            </div>
          </div>
        </div>
      </Fade>
      <CampaignRuntime
        event={event}
        updateDate={updateDate}
        dates={dates}
        totalRuntime={totalRuntime}
      />
      <OpenCampaigns
        eventId={event.id}
        campaigns={openCampaigns}
        selectedTcs={selectedTcs}
        setSelectedTcs={setSelectedTcs}
        totalUniqueTcs={totalUniqueTcs}
        setupProcessId={isCampaignSetupInProgress ? setup.id : null}
        eventHasActiveCampaigns={eventHasRunningCampaigns}
        refetchCampaigns={refetchCampaigns}
      />
      <div className="recommendations-page_cta-btns_row order-1 order-lg-3">
        <SecondaryButton
          disabled={
            !selectedTcs.length ||
            !hasWaveAccess ||
            budgetLoadingRef.current === true ||
            itcStatusError ||
            itcStatusLoading ||
            ['running', 'failed'].includes(itcMappedStatus)
          }
          disabledTitle={!selectedTcs.length ? '' : null}
          className="bt"
          onClick={handleNextClick}
          text={
            isCampaignSetupInProgress
              ? t('Recommendations.btnContinueSetup')
              : t('Recommendations.btnStartSetup')
          }
        />
      </div>
      <PortalModal
        isOpen={isConfirmModalOpen}
        onClickOutside={() => setIsConfirmModalOpen(false)}
      >
        <ConfirmDeselectTcModal
          tcs={deselectedTcs}
          onCancel={() => setIsConfirmModalOpen(false)}
          onConfirm={() => {
            setIsConfirmModalOpen(false)
            startOrContinueSetup()
          }}
        />
      </PortalModal>
    </div>
  )
}

CampaignSetup.propTypes = {
  event: PropTypes.object,
  setup: PropTypes.object,
  campaigns: PropTypes.object,
  suggestedTcs: PropTypes.object,
  selectedTcs: PropTypes.arrayOf(Object),
  setSelectedTcs: PropTypes.func,
  initialSelectedTcs: PropTypes.arrayOf(PropTypes.string),
  initialBudget: PropTypes.number,
  initialGoal: PropTypes.string,
  dispatchSuggestedTcsAction: PropTypes.func,
  dispatchSetupAction: PropTypes.func,
  openCampaigns: PropTypes.arrayOf(Object),
  isCampaignSetupInProgress: PropTypes.bool,
  setSuggestedTcsChanged: PropTypes.func,
  refetchCampaigns: PropTypes.func.isRequired,
}

export default CampaignSetup
